import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import Drawer from "@mui/material/Drawer";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import type { DictationState } from "../dictation/dictation";
import { unavailableReason } from "../dictation/dictation";
import { PButton } from "../ui/PButton";
import { PRIME_GLYPHS, type PrimeGlyphName } from "../ui/prime-glyphs";
import { age, mediumDate, upper, type AssistPatron } from "./assist-logic";
import { EXTRA_GLYPHS, type ExtraGlyphName } from "./glyphs";

// THE SMALL PIECES `pillars-assist.component.html` IS BUILT FROM.

// AN `<i class="pi pi-*">`: `.pi` IS 1REM IN THE THEME WHATEVER THE TEXT AROUND IT, UNLESS A
// PRIMEFLEX SIZE CLASS (`!important`) OR AN INLINE SIZE SAYS OTHERWISE
export function Pi({ name, size = "1rem", className = "", spin = false, style }: { name: PrimeGlyphName | ExtraGlyphName; size?: string; className?: string; spin?: boolean; style?: CSSProperties }) {
  const glyph = name in EXTRA_GLYPHS ? EXTRA_GLYPHS[name as ExtraGlyphName] : PRIME_GLYPHS[name as PrimeGlyphName];
  return (
    <i className={`${spin ? "pi-spin " : ""}${className}`} style={{ display: "inline-block", lineHeight: 1, fontStyle: "normal", ...style }} aria-hidden="true">
      <svg width={size} height={size} viewBox={`0 0 ${glyph.adv} 1024`} fill="currentColor" focusable="false" style={{ display: "block" }}>
        <path transform="translate(0 960) scale(1 -1)" d={glyph.d} />
      </svg>
    </i>
  );
}

// THE `pi` BEFORE A `text-xs` LINE. THE ICON FONT'S METRICS MAKE THAT LINE 14.75PX TALL IN
// ANGULAR, SO THE STAND-IN HOLDS IT THERE
function LineIcon({ name }: { name: PrimeGlyphName }) {
  return (
    <span className="mr-1" style={{ display: "inline-flex", alignItems: "center", height: "14.75px", verticalAlign: "top" }}>
      <Pi name={name} />
    </span>
  );
}

// THE MICROPHONE CONTROLS BOTH PROMPT FIELDS CARRY: A SPINNER WHILE THE PERMISSION PROMPT IS
// OPEN, ANOTHER WHILE TRANSCRIBING, THE START BUTTON (ONLY WHEN DICTATION IS AVAILABLE), THE
// STOP BUTTON (NEVER GATED ON AVAILABILITY, SO A WITHDRAWAL CANNOT STRAND SOMEONE MID-SENTENCE),
// AND AN INFO ICON SAYING WHY THERE IS NO MICROPHONE
export function MicControls({ state: s, onStart, onStop }: { state: DictationState | null; onStart: () => void; onStop: () => void }) {
  if (!s) return null;
  const reason = unavailableReason(s);
  return (
    <>
      {s.isStarting && (
        <div className="px-2">
          <Tooltip title="Waiting for microphone access..." placement="top">
            <span style={{ display: "inline-block" }}>
              <Pi name="spinner" spin className="text-500" />
            </span>
          </Tooltip>
        </div>
      )}
      {(s.isStopping || s.isTranscribing) && (
        <div className="px-2">
          <Tooltip title="Transcribing..." placement="top">
            <span style={{ display: "inline-block" }}>
              <Pi name="spinner" spin />
            </span>
          </Tooltip>
        </div>
      )}
      {s.isAvailable && !s.isBusy && (
        <div>
          <Tooltip title="Start dictation" placement="top">
            <span style={{ display: "inline-flex" }}>
              <PButton icon="microphone" rounded variant="text" size="lg" hostElement ariaLabel="Start dictation" onClick={onStart} />
            </span>
          </Tooltip>
        </div>
      )}
      {s.isRecording && (
        <div>
          <Tooltip title="Stop dictation and transcribe" placement="top">
            <span className="fadein animation-duration-1000 animation-iteration-infinite" style={{ display: "inline-flex" }}>
              <PButton icon="stop-circle" rounded variant="text" severity="danger" size="lg" hostElement ariaLabel="Stop dictation and transcribe" onClick={onStop} />
            </span>
          </Tooltip>
        </div>
      )}
      {!s.isAvailable && !s.isRecording && (
        <div className="px-2">
          <Tooltip title={reason ?? ""} placement="top">
            <span style={{ display: "inline-block" }}>
              <Pi name="info-circle" className="text-500" />
            </span>
          </Tooltip>
        </div>
      )}
    </>
  );
}

// THE DICTATION ERROR LINE, WITH "ALLOW MICROPHONE" WHEN ACCESS WAS DENIED, AND THE SHARED
// `ai-unavailable-notice`
export function PromptNotices({ state: s, aiAvailable, onAllowMic }: { state: DictationState | null; aiAvailable: boolean; onAllowMic: () => void }) {
  return (
    <>
      {s?.error && (
        <div className="mt-2 text-xs text-red-500">
          <LineIcon name="exclamation-circle" />
          {s.error}
          {s.permissionDenied && s.isSupported && (
            <button type="button" className="pillars-assist-link" onClick={onAllowMic}>
              Allow microphone
            </button>
          )}
        </div>
      )}
      {!aiAvailable && (
        <div className="my-2 flex align-items-center gap-2 text-sm text-orange-500">
          <Pi name="exclamation-triangle" />
          <span>AI is temporarily unavailable. Reconnecting…</span>
        </div>
      )}
    </>
  );
}

// THE PROMPT FIELD: `pInputText`, "Find a player..."
export function PromptInput({ value, onChange, onKeyDown }: { value: string; onChange: (v: string) => void; onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void }) {
  return <input type="text" className="p-inputtext" placeholder="Find a player..." aria-label="Find a player" value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} />;
}

// THE SEND ADDON: A LARGE ROUND TEXT `pi-arrow-circle-up`
export function SendAddon({ disabled, onSend }: { disabled: boolean; onSend: () => void }) {
  return (
    <div className="pillars-assist-addon">
      <PButton icon="arrow-circle-up" rounded variant="text" severity="primary" size="lg" hostElement ariaLabel="Send" disabled={disabled} onClick={onSend} />
    </div>
  );
}

// `p-overlayPanel`, `[dismissable]="false"`, `[focusOnShow]="true"`: APPENDED TO THE BODY AND
// PLACED UNDER ITS TARGET BY `DomHandler.absolutePosition` (ABOVE IT IF IT WOULD RUN OFF THE
// BOTTOM, PULLED LEFT IF IT WOULD RUN OFF THE RIGHT). AN OUTSIDE CLICK LEAVES IT OPEN; ESCAPE,
// AND A WINDOW RESIZE ON A NON-TOUCH DEVICE, HIDE IT.
export function OverlayPanel({ open, anchor, onHide, children }: { open: boolean; anchor: HTMLElement | null; onHide: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; arrow: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!open || !anchor || !el) {
      setPos(null);
      return;
    }
    const t = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const sx = window.scrollX;
    const sy = window.scrollY;
    let top: number;
    if (t.top + t.height + h > window.innerHeight) {
      top = t.top + sy - h;
      if (top < 0) top = sy;
    } else {
      top = t.bottom + sy;
    }
    const left = t.left + w > window.innerWidth ? Math.max(0, t.left + sx + t.width - w) : t.left + sx;
    const arrow = left < t.left + sx ? t.left + sx - left : 0;
    setPos({ top, left, arrow });
    // `focusOnShow`: THE FIRST FOCUSABLE ELEMENT IN THE PANEL
    el.querySelector<HTMLElement>("button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])")?.focus();
  }, [open, anchor]);

  const hideRef = useRef(onHide);
  hideRef.current = onHide;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hideRef.current();
    };
    const touch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const onResize = () => {
      if (!touch) hideRef.current();
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="Pillars Assist"
      className="pillars-assist-overlay shadow-3 w-20rem"
      style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden", ["--overlayArrowLeft" as string]: `${pos?.arrow ?? 0}px` }}
    >
      <div className="pillars-assist-overlay-content">{children}</div>
    </div>,
    document.body,
  );
}

// A NON-MODAL `p-sidebar` WITH `[showCloseIcon]="false"`, WHICH `ui/PSidebar` CANNOT DRAW: THE
// SAME PAPER, AN EMPTY 1REM 1.25REM HEADER, AND CONTENT PADDED 0 1.25REM 1.25REM. ESCAPE AND
// OUTSIDE CLICKS NEVER CLOSE IT (`[dismissible]` / `[closeOnEscape]` FALSE)
export function BareSidebar({ open, position, width, ariaLabel, children }: { open: boolean; position: "left" | "right"; width: string; ariaLabel: string; children: ReactNode }) {
  return (
    <Drawer
      anchor={position}
      open={open}
      hideBackdrop
      disableScrollLock
      disableEnforceFocus
      sx={{ pointerEvents: "none", "& .MuiDrawer-paper": { pointerEvents: "auto" } }}
      slotProps={{
        paper: {
          "aria-label": ariaLabel,
          sx: {
            width,
            maxWidth: "100vw",
            border: "1px solid rgba(0, 0, 0, 0.2)",
            borderRadius: 0,
            boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.02), 0px 0px 2px rgba(0, 0, 0, 0.05), 0px 1px 4px rgba(0, 0, 0, 0.08)",
          },
        },
      }}
    >
      <div style={{ padding: "1rem 1.25rem" }} />
      <div style={{ padding: "0 1.25rem 1.25rem 1.25rem", flex: "1 1 auto", overflowY: "auto" }}>{children}</div>
    </Drawer>
  );
}

// THE PATRON `p-dropdown` (`optionLabel="name"`, "Select a player"): THE CHOSEN ONE READS
// "NAME - CARD", EACH OPTION IS A RULED BLOCK OF BOLD NAME, CARD ID AND BIRTH DATE WITH AGE
export function PatronSelect({ patrons, value, onChange }: { patrons: AssistPatron[]; value: number | undefined; onChange: (index: number) => void }) {
  return (
    <Select
      value={value ?? ""}
      displayEmpty
      onChange={(e) => onChange(Number(e.target.value))}
      inputProps={{ "aria-label": "Select a player" }}
      IconComponent={(p) => (
        <span {...p} style={{ ...p.style, top: 0, right: 0, height: "100%", width: "2.357rem", transform: "none", display: "grid", placeItems: "center", color: "#6c757d", pointerEvents: "none" }}>
          <Pi name="chevron-down" />
        </span>
      )}
      renderValue={(v) => {
        const p = typeof v === "number" ? patrons[v] : undefined;
        return p ? `${upper(p.name)} - ${upper(p.cardId)}` : <span style={{ color: "#6c757d" }}>Select a player</span>;
      }}
      MenuProps={{ slotProps: { paper: { sx: { borderRadius: "4px", mt: 0.25, boxShadow: "0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12)" } } } }}
      sx={{
        width: "100%",
        fontSize: "1rem",
        borderRadius: "4px",
        "& .MuiSelect-select.MuiSelect-select": {
          height: "auto",
          minHeight: 0,
          padding: "calc(0.5rem + 1px) calc(0.75rem + 1px)",
          paddingRight: "2.357rem !important",
          lineHeight: "normal",
        },
      }}
    >
      {patrons.map((p, i) => (
        <MenuItem key={i} value={i} sx={{ fontSize: "1rem", padding: "0.5rem 0.75rem", minHeight: "auto", display: "block", whiteSpace: "normal" }}>
          <div className="pt-3 flex flex-column border-top-1 surface-border">
            <div className="font-bold">{upper(p.name)}</div>
            <div className="mt-1 text-sm uppercase">Card ID: {upper(p.cardId)}</div>
            {p.birthdate && (
              <div className="mt-1 text-sm uppercase">
                Birth Date: {mediumDate(p.birthdate)} - {age(p.birthdate)} years old
              </div>
            )}
          </div>
        </MenuItem>
      ))}
    </Select>
  );
}
